import {
  type CSSProperties,
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Layout, type RowProps, theme } from 'antd';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import { useNavMenuContext } from 'pos-client/contexts/NavMenuContext.tsx';
import { AppFooter } from 'pos-client/layouts/app/AppFooter.tsx';

export interface PosPageContentProps {
  row?: boolean;
  rowProps?: RowProps;
  children: ReactNode;
  style?: CSSProperties;
  footerStyle?: CSSProperties;
  hideFooter?: boolean;
}

export const PosPageContent = memo(
  ({
    children,
    row = false,
    rowProps,
    style,
    footerStyle,
    hideFooter,
  }: PosPageContentProps) => {
    const { token } = theme.useToken();
    const contentRef = useRef<HTMLDivElement>(null);
    const { headerHeight } = useNavMenuContext();
    const isRow = row || !!rowProps;

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, []);

    const defaultLayoutStyle: CSSProperties = useMemo(
      () => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        width: '100%',
        minHeight: `calc(100vh - ${headerHeight}px)`,
        paddingTop: token.paddingLG,
        paddingLeft: token.paddingLG,
        paddingRight: token.paddingLG,
        paddingBottom: hideFooter ? token.paddingLG : 0,
        flex: 1,
      }),
      [headerHeight, hideFooter, token]
    );

    return (
      <Layout.Content
        ref={contentRef}
        style={{ ...defaultLayoutStyle, ...style }}
      >
        {isRow ? (
          <PosGrid gutter={[token.paddingLG, token.paddingLG]} {...rowProps}>
            {children}
          </PosGrid>
        ) : (
          children
        )}
        {!hideFooter && <AppFooter style={footerStyle} />}
      </Layout.Content>
    );
  }
);
